import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "../../firebase/firebase";

const DOCUMENTS = [
  {
    label: "Passport",
    expiryKey: "passportExpiry",
    files: [
      { key: "passportFile", label: "Page 1" },
      { key: "passportFileBack", label: "Page 2" },
    ],
  },
  {
    label: "Emirates ID",
    expiryKey: "emiratesIdExpiry",
    files: [
      { key: "emiratesIdFile", label: "Front" },
      { key: "emiratesIdFileBack", label: "Back" },
    ],
  },
  {
    label: "Driving License",
    expiryKey: "licenseExpiry",
    files: [
      { key: "licenseFile", label: "Front" },
      { key: "licenseFileBack", label: "Back" },
    ],
  },
  {
    label: "RTA Permit",
    expiryKey: "rtaPermitExpiry",
    files: [{ key: "rtaPermitFile", label: "File" }],
  },
  {
    label: "RTA Training",
    expiryKey: "rtaTrainingExpiry",
    files: [{ key: "rtaTrainingFile", label: "File" }],
  },
  {
    label: "Health Insurance",
    expiryKey: "healthInsuranceExpiry",
    files: [{ key: "healthInsuranceFile", label: "File" }],
  },
];

const ALL_EXPIRY_KEYS = DOCUMENTS.map((d) => d.expiryKey);

function dubaiDateString(value) {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  const d = value?.toDate ? value.toDate() : value?.seconds ? new Date(value.seconds * 1000) : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Dubai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function safeFileName(name) {
  return String(name || "file").replace(/[^0-9A-Za-z._()\-]/g, "_");
}

function validateFile(file) {
  if (!file) return "";
  if (file.size >= 10 * 1024 * 1024) return "File must be smaller than 10 MB.";
  const type = String(file.type || "");
  if (!(type.startsWith("image/") || type === "application/pdf")) {
    return "Only images or PDF files are allowed.";
  }
  return "";
}

async function uploadDriverDocument(driverId, fieldKey, file) {
  const path = `driverDocuments/${driverId}/${fieldKey}/${Date.now()}_${safeFileName(file.name)}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, { contentType: file.type || "application/octet-stream" });
  return getDownloadURL(storageRef);
}

export default function DriverDocumentsEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const allowed = ["docs_link", "admin", "md"].includes(role);

  const driverRef = useMemo(() => (id ? doc(db, "drivers", id) : null), [id]);
  const [driver, setDriver] = useState(null);
  const [expiry, setExpiry] = useState({});
  const [dirtyExpiry, setDirtyExpiry] = useState(() => new Set());
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  useEffect(() => {
    if (!allowed || !driverRef) return undefined;
    let alive = true;

    (async () => {
      try {
        const snap = await getDoc(driverRef);
        if (!alive) return;
        if (!snap.exists()) {
          setDriver(null);
          setError("Employee not found.");
          setLoading(false);
          return;
        }
        const data = { id: snap.id, ...snap.data() };
        const initialExpiry = {};
        ALL_EXPIRY_KEYS.forEach((key) => {
          initialExpiry[key] = dubaiDateString(data[key]);
        });
        setDriver(data);
        setExpiry(initialExpiry);
        setLoading(false);
      } catch (err) {
        console.error("Driver document detail failed:", err);
        if (!alive) return;
        setError("Unable to load this employee. Please check the published Firestore rules.");
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [allowed, driverRef]);

  const setExpiryValue = (key, value) => {
    setExpiry((old) => ({ ...old, [key]: value }));
    setDirtyExpiry((old) => {
      const next = new Set(old);
      next.add(key);
      return next;
    });
    setInfo("");
  };

  const setFileValue = (key, file) => {
    if (file) {
      const validation = validateFile(file);
      if (validation) {
        setError(`${file.name}: ${validation}`);
        return;
      }
    }
    setError("");
    setInfo("");
    setFiles((old) => ({ ...old, [key]: file || null }));
  };

  const save = async () => {
    if (!driverRef || !driver) return;
    setSaving(true);
    setError("");
    setInfo("");

    try {
      const updates = {};

      for (const key of dirtyExpiry) {
        updates[key] = expiry[key] || null;
      }

      for (const docDef of DOCUMENTS) {
        for (const fileDef of docDef.files) {
          const file = files[fileDef.key];
          if (!file) continue;
          const validation = validateFile(file);
          if (validation) throw new Error(`${file.name}: ${validation}`);
          updates[fileDef.key] = await uploadDriverDocument(driver.id, fileDef.key, file);
        }
      }

      const changedDocumentFields = Object.keys(updates);
      if (changedDocumentFields.length === 0) {
        setInfo("No document changes to save.");
        return;
      }

      updates.documentsUpdatedAt = serverTimestamp();
      updates.documentsUpdatedBy = auth.currentUser?.email || auth.currentUser?.uid || "";

      await updateDoc(driverRef, updates);

      const refreshed = await getDoc(driverRef);
      if (refreshed.exists()) {
        const data = { id: refreshed.id, ...refreshed.data() };
        const refreshedExpiry = {};
        ALL_EXPIRY_KEYS.forEach((key) => {
          refreshedExpiry[key] = dubaiDateString(data[key]);
        });
        setDriver(data);
        setExpiry(refreshedExpiry);
      }

      setFiles({});
      setDirtyExpiry(new Set());
      setInfo("Documents saved directly to the employee record. No approval step is required.");
    } catch (e) {
      console.error("Document save failed:", e);
      if (String(e?.code || "").includes("permission-denied")) {
        setError("Save was blocked by security rules. Please publish the supplied Firestore and Storage rules.");
      } else {
        setError(e?.message || "Unable to save documents. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) return <div style={{ padding: 24, color: "#b91c1c", fontWeight: 800 }}>Not authorized.</div>;
  if (loading) return <div style={{ padding: 24 }}>Loading employee documents…</div>;
  if (!driver) return <div style={{ padding: 24, color: "#b91c1c", fontWeight: 800 }}>{error || "Employee not found."}</div>;

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div>
          <button onClick={() => navigate("/drivers")} style={backButton}>← Back to Driver Documents</button>
          <h2 style={{ margin: "14px 0 4px" }}>{driver.fullName || "Employee Documents"}</h2>
          <div style={{ color: "#64748b", fontSize: 13 }}>
            {driver.company || "-"} · Status: <b>{driver.status || "-"}</b> · Car: <b>{driver.assignedCarNumber || "No Car"}</b>
          </div>
        </div>
        <div style={lockedNote}>
          <b>Documents-only access</b><br />
          Status, company, assignment and salary cannot be changed here.
        </div>
      </div>

      {info ? <div style={successStyle}>{info}</div> : null}
      {error ? <div style={errorStyle}>{error}</div> : null}

      <div style={{ display: "grid", gap: 14, marginTop: 18 }}>
        {DOCUMENTS.map((docDef) => (
          <section key={docDef.label} style={cardStyle}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(160px,0.8fr) minmax(170px,0.7fr) minmax(320px,1.5fr)", gap: 16, alignItems: "start" }}>
              <div>
                <div style={{ fontWeight: 900, color: "#0f172a", fontSize: 16 }}>{docDef.label}</div>
                <div style={{ color: "#64748b", fontSize: 12, marginTop: 5 }}>
                  Update only when the document or expiry changes.
                </div>
              </div>

              <div>
                <label style={labelStyle}>Expiry date</label>
                <input
                  type="date"
                  value={expiry[docDef.expiryKey] || ""}
                  onChange={(e) => setExpiryValue(docDef.expiryKey, e.target.value)}
                  style={dateInputStyle}
                />
              </div>

              <div style={{ display: "grid", gap: 10 }}>
                {docDef.files.map((fileDef) => (
                  <div key={fileDef.key} style={{ padding: 10, border: "1px solid #e2e8f0", borderRadius: 9, background: "#f8fafc" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontWeight: 800, color: "#334155", fontSize: 13 }}>{fileDef.label}</div>
                        {driver[fileDef.key] ? (
                          <a href={driver[fileDef.key]} target="_blank" rel="noreferrer" style={{ display: "inline-block", marginTop: 4, color: "#2563eb", fontSize: 12, fontWeight: 700 }}>
                            View current file
                          </a>
                        ) : (
                          <div style={{ marginTop: 4, color: "#b91c1c", fontSize: 12, fontWeight: 700 }}>No file uploaded</div>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => setFileValue(fileDef.key, e.target.files?.[0] || null)}
                      />
                    </div>
                    {files[fileDef.key] ? (
                      <div style={{ marginTop: 6, color: "#065f46", fontSize: 12, fontWeight: 700 }}>
                        New file selected: {files[fileDef.key].name}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </section>
        ))}
      </div>

      <div style={{ position: "sticky", bottom: 12, marginTop: 18, padding: 14, borderRadius: 12, background: "rgba(255,255,255,0.96)", border: "1px solid #cbd5e1", boxShadow: "0 10px 30px rgba(15,23,42,0.12)", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12 }}>
        <span style={{ marginRight: "auto", color: "#64748b", fontSize: 12 }}>
          Saving updates the driver record immediately. There is no approval step.
        </span>
        <button disabled={saving} onClick={save} style={{ ...saveButton, opacity: saving ? 0.65 : 1, cursor: saving ? "not-allowed" : "pointer" }}>
          {saving ? "Saving…" : "Save Documents"}
        </button>
      </div>
    </div>
  );
}

const backButton = { border: "1px solid #cbd5e1", background: "white", borderRadius: 8, padding: "7px 10px", color: "#334155", fontWeight: 800, cursor: "pointer" };
const lockedNote = { padding: "10px 12px", borderRadius: 10, background: "#eef2ff", border: "1px solid #c7d2fe", color: "#3730a3", fontSize: 12, lineHeight: 1.5 };
const cardStyle = { background: "white", border: "1px solid #e2e8f0", borderRadius: 13, padding: 16, boxShadow: "0 3px 10px rgba(15,23,42,0.04)" };
const labelStyle = { display: "block", fontSize: 12, fontWeight: 800, color: "#475569", marginBottom: 6 };
const dateInputStyle = { width: "100%", boxSizing: "border-box", padding: "9px 10px", borderRadius: 8, border: "1px solid #cbd5e1", background: "white" };
const saveButton = { border: 0, borderRadius: 9, padding: "10px 16px", background: "#059669", color: "white", fontWeight: 900 };
const successStyle = { marginTop: 16, padding: 12, borderRadius: 10, background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46", fontWeight: 700 };
const errorStyle = { marginTop: 16, padding: 12, borderRadius: 10, background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", fontWeight: 700 };
